import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useRegisterInput from '@afosto/app-components-react/Form/useRegisterInput';
import { useShipmentFormContext } from '../ShipmentFormProvider';
import isEqual from 'lodash.isequal';
import LocationFormSection from '../LocationFormSection';
import ShipmentFormSection from '../ShipmentFormSection';
import AddressFormSection from '@afosto/app-components-react/AddressFormSection';
import ContactFormSection from '@afosto/app-components-react/ContactFormSection';
import Form from '@afosto/app-components-react/Form';
import FormActions from '@afosto/app-components-react/Form/FormActions';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import OrganisationFormSection from '@afosto/app-components-react/OrganisationFormSection';
import ShippingMethodFormSection from '@afosto/app-components-react/ShippingMethodFormSection';
import StackItemList from '@afosto/app-components-react/StackItemList';
import updateObjectInArray from '@afosto/utils/array/updateObjectInArray';
import getFullName from '@afosto/utils/getFullName';
import uuid from '@afosto/utils/uuid';
import validationSchema, { updateValidationSchema } from './validations';
import translations from './translations';

const ShipmentForm = ({ onSubmit, onCancel }) => {
  const intl = useIntl();
  const shipmentFormContext = useShipmentFormContext();
  const { contact, fromAddress, fromLocation, locations, method, shipment, toAddress, toLocation } =
    shipmentFormContext ?? {};
  const form = useForm({
    validationSchema: shipment?.id ? updateValidationSchema : validationSchema,
    defaultValues: {
      fromType: shipment?.addressing?.from?.entity_type ?? 'location',
      toType: shipment?.addressing?.to?.entity_type ?? 'address',
      fromLocation: fromLocation ?? null,
      toLocation: toLocation ?? null,
      fromAddress: fromAddress ?? null,
      toAddress: toAddress ?? null,
      type: shipment?.is_outbound === false ? 'inbound' : 'outbound',
      contact: contact ?? null,
      organisation: null,
      method: method ?? null,
      expectedAt: shipment?.expected_at ?? null,
      shipAt: shipment?.ship_at ?? null,
      items: [],
    },
    onSubmit,
    onCancel,
  });

  useRegisterInput('fromLocation', true, form);
  useRegisterInput('fromType', true, form);
  useRegisterInput('toType', true, form);
  useRegisterInput('items', true, form);
  useRegisterInput('method', true, form);

  const { setValue, watch } = form;
  const fromTypeValue = watch('fromType');
  const toTypeValue = watch('toType');
  const typeValue = watch('type');
  const contactValue = watch('contact');
  const organisationValue = watch('organisation');
  const itemsValue = watch('items');
  const methodValue = watch('method');
  const toAddressValue = watch('toAddress');
  const fromLocationValue = watch('fromLocation');
  const toLocationValue = watch('toLocation');
  const formRef = useRef(form);

  useEffect(() => {
    formRef.current = form;
  });

  const countryCodeValue = toAddressValue?.country_code;
  const countryCodeValueRef = useRef(countryCodeValue);
  const countryIsDifferent = !isEqual(countryCodeValue, countryCodeValueRef.current);

  useEffect(() => {
    if (countryIsDifferent) {
      formRef.current.setValue('method', null);
    }
  }, [countryIsDifferent]);

  useEffect(() => {
    countryCodeValueRef.current = countryCodeValue;
  });

  useEffect(() => {
    let isMounted = true;

    if (typeValue === 'outbound' && !fromLocationValue && locations.length === 1) {
      const [firstLocation] = locations;

      if (isMounted) {
        setValue('fromLocation', {
          ...firstLocation,
          label: firstLocation?.name,
          value: firstLocation?.id,
        });
      }
    }

    return () => {
      isMounted = false;
    };
  }, [typeValue, fromLocationValue, locations, setValue]);

  useEffect(() => {
    let isMounted = true;

    if (typeValue === 'inbound' && !toLocationValue && locations.length === 1) {
      const [firstLocation] = locations;

      if (isMounted) {
        setValue('toLocation', {
          ...firstLocation,
          label: firstLocation?.name,
          value: firstLocation?.id,
        });
      }
    }

    return () => {
      isMounted = false;
    };
  }, [typeValue, toLocationValue, locations, setValue]);

  /**
   * Handle creating a new item.
   * @param {string} sku
   * @param {string} description
   * @param {number} quantity
   * @param {string} image
   * @returns {Promise}
   */
  const handleCreateItem = ({ sku, description, quantity, product }) => {
    const generatedID = uuid();
    const item = {
      id: generatedID,
      reference: generatedID,
      sku,
      description,
      quantity: parseInt(quantity, 10),
      image: product?.image,
    };
    setValue('items', [...itemsValue, item]);
  };

  /**
   * Handle removing an item.
   * @param {array} itemIds
   */
  const handleRemoveItems = itemIds => {
    const newItems = itemsValue.filter(({ reference }) => !itemIds.includes(reference));
    setValue('items', newItems);
  };

  /**
   * Handle the quantity change of the item.
   * @param {object} item
   */
  const handleQuantityChange = item => event => {
    const { value } = event.target;
    const updatedItems = updateObjectInArray(itemsValue, { ...item, quantity: value }, 'reference');

    setValue('items', updatedItems);
  };

  const handleUpdateContact = updatedContact => {
    setValue('contact', {
      ...updatedContact,
      label: getFullName(updatedContact),
      value: updatedContact.id,
    });
  };

  const handleUpdateOrganisation = updatedOrganisation => {
    setValue('organisation', {
      ...updatedOrganisation,
      label: updatedOrganisation.name,
      value: updatedOrganisation.id,
    });
  };

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form} renderActions={false}>
        <ShipmentFormSection shipment={shipment} locations={locations} />

        {typeValue !== 'transfer' && <ContactFormSection readOnly={!!shipment?.id} />}

        {(fromTypeValue === 'address' || toTypeValue === 'address') && (
          <OrganisationFormSection
            contact={contactValue}
            onUpdateContact={handleUpdateContact}
            autoFillFromContact={!shipment?.id}
          />
        )}

        {fromTypeValue === 'location' && (
          <LocationFormSection
            name="fromLocation"
            title={intl.formatMessage(translations.from.title)}
            locations={locations}
            type={typeValue}
          />
        )}

        {fromTypeValue === 'address' && (
          <AddressFormSection
            contact={contactValue}
            setContact={handleUpdateContact}
            organisation={organisationValue}
            setOrganisation={handleUpdateOrganisation}
            type="shipping"
            name="fromAddress"
            title={intl.formatMessage(translations.from.title)}
          />
        )}

        {toTypeValue === 'location' && (
          <LocationFormSection
            name="toLocation"
            title={intl.formatMessage(translations.to.title)}
            locations={locations}
            type={typeValue}
          />
        )}

        {toTypeValue === 'address' && (
          <AddressFormSection
            contact={contactValue}
            setContact={handleUpdateContact}
            organisation={organisationValue}
            setOrganisation={handleUpdateOrganisation}
            type="shipping"
            name="toAddress"
            title={intl.formatMessage(translations.to.title)}
          />
        )}

        {!shipment?.id && (
          <StackItemList
            items={itemsValue}
            allowCreate
            onCreate={handleCreateItem}
            allowRemove
            onRemove={handleRemoveItems}
            allowChangeQuantity
            onChangeQuantity={handleQuantityChange}
          />
        )}

        {typeValue === 'outbound' && (
          <ShippingMethodFormSection
            shipping={methodValue}
            shippingAddress={toAddressValue}
            setShipping={shipping => setValue('method', shipping)}
            showSplitOption={false}
          />
        )}

        <FormActions
          primaryAction={{
            label: intl.formatMessage(translations.formActions.save),
            onClick: form.onSubmit,
            type: 'submit',
            color: 'primary',
            variant: 'contained',
            disabled: !shipment?.id && itemsValue.length === 0,
          }}
          secondaryAction={
            onCancel
              ? {
                  label: intl.formatMessage(translations.formActions.cancel),
                  onClick: form.onCancel,
                  variant: 'contained',
                }
              : null
          }
        />
      </Form>
    </LoadingWrapper>
  );
};

ShipmentForm.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

ShipmentForm.defaultProps = {
  onSubmit: undefined,
  onCancel: undefined,
};

export default ShipmentForm;
